/**
 * @fileoverview One location as a line: picture, name, how many plants, whether the
 * weather reaches it. Ten beds in the height of one card. A tap opens the
 * bed's card in a pop-up (B157, BedQuickLook), as a plant's row opens its card.
 *
 * The picture is the server's cover, as sent (its thumbnail): the bed's
 * newest photo, else its chosen cover, else Grow's default (bug B2).
 *
 * Where: LocationGrid, the list density.
 * The Seed vault (D69) has a seed-packet icon before its name, drawn only where the server says is_vault (VaultIcon).
 *
 * API: none.
 * Words (en/es): locations.tile.open, locations.card.plants.count,
 * locations.card.plants.count_plural, locations.sheltered.short,
 * locations.sheltered.long, locations.archive.restore.
 *
 * @module LocationRow
 */
import React from 'react';
import { CloudOff, MapPin, Undo2 } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { Pill } from '@/shared/components/ui/Pill';
import type { Location } from '@/shared/api/locations';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';
import { VaultIcon } from '@/features/seed-orders/VaultBits';

/** One location's line, and what pressing it does. */
interface LocationRowProps {
  /** The bed. */
  location: Location;
  /** Opens the bed's card on it (the page's pop-up); a no-op on the archive page. */
  onOpen: (location: Location) => void;
  /** Drawn faded, with a restore button, on the archive page. */
  archived?: boolean;
  /** Puts it back among the live beds (the archive page only). */
  onRestore?: (location: Location) => void;
  /** This one is being restored: its button waits. */
  isRestoring?: boolean;
}

/**
 * How many of a location's plants are still growing (status 1), not archived.
 *
 * @param location - The bed.
 */
const liveCount = (location: Location): number =>
  location.plants.filter((plant) => plant.status === 1).length;

/**
 * A location as one line of the list density.
 *
 * @param props - LocationRowProps.
 */
export const LocationRow: React.FC<LocationRowProps> = ({
  location, onOpen, archived, onRestore, isRestoring,
}) => {
  const { reactTranslate: t } = useTranslation();
  const [photoFailed, setPhotoFailed] = React.useState(false);
  /** The server's cover, at thumbnail size. */
  const picture = location.cover?.thumbUrl ?? null;
  React.useEffect(() => setPhotoFailed(false), [picture]);
  const count = liveCount(location);

  return (
    <div className={cn(
      'flex items-center gap-3 px-3 py-2 transition-colors hover:bg-accent/40',
      archived && 'opacity-70',
    )}>
      <button
        type="button"
        onClick={() => onOpen(location)}
        aria-label={t('locations.tile.open', { name: location.name })}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-sm bg-muted data-[small=true]:bg-transparent">
          {picture && !photoFailed ? (
            <FramedPhoto src={picture} alt="" loading="lazy" onError={() => setPhotoFailed(true)} />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <MapPin className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm font-medium leading-snug font-display">
            {/* D69: the seed-packet icon, only where the server says is_vault. */}
            {location.is_vault && <VaultIcon className="mr-1 inline-block h-3.5 w-3.5 align-[-2px]" />}
            {location.name}
          </p>
          {location.description && (
            <p className="truncate text-xs text-muted-foreground">{location.description}</p>
          )}
        </div>
        <p className="shrink-0 text-xs text-muted-foreground">
          {count === 1 ? t('locations.card.plants.count', { count }) : t('locations.card.plants.count_plural', { count })}
        </p>
        {location.sheltered && (
          <Pill size="sm" tone="outline" icon={<CloudOff />} className="hidden shrink-0 sm:inline-flex" title={t('locations.sheltered.long')}>
            {t('locations.sheltered.short')}
          </Pill>
        )}
      </button>

      {archived && onRestore && (
        <button
          type="button"
          onClick={() => onRestore(location)}
          disabled={isRestoring}
          aria-label={t('locations.archive.restore')}
          title={t('locations.archive.restore')}
          className="shrink-0 rounded-sm p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          <Undo2 className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};
